import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Eye, Edit, Trash2, Loader2, LogOut, Users, MessageSquare, ChevronDown, Copy, Shield } from "lucide-react";
import { listInvitations, deleteInvitation, listRsvps } from "@/lib/api";
import { getTheme } from "@/lib/themes";
import { useAuth } from "@/lib/auth";
import { resolveImageUrl } from "@/components/ImageUpload";
import { formatTime12h } from "@/lib/utils";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState({}); // { invitation_id: [rsvps] }
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await listInvitations();
      setInvs(data);
      const map = {};
      await Promise.all(
        data.map(async (i) => {
          try {
            const r = await listRsvps(i.id);
            map[i.id] = r;
          } catch { map[i.id] = []; }
        })
      );
      setRsvps(map);
    } catch (e) {
      toast.error("No se pudo cargar el panel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta invitación y sus registros?")) return;
    try {
      await deleteInvitation(id);
      toast.success("Eliminada");
      setInvs((p) => p.filter((i) => i.id !== id));
    } catch { toast.error("No se pudo eliminar"); }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/i/${slug}`);
    toast.success("Link copiado");
  };

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const totalConfirmed = Object.values(rsvps).reduce((sum, arr) => sum + arr.filter((r) => r.attending).reduce((a, r) => a + (r.guests_count || 1), 0), 0);
  const totalRsvps = Object.values(rsvps).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="min-h-screen bg-cream">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-8 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="btn-ghost" data-testid="dashboard-back-btn">
          <ArrowLeft size={16} /> Inicio
        </button>
        <div className="flex items-center gap-3">
          {user && (
            <div className="hidden md:flex items-center gap-3 mr-2">
              {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
              <div className="text-right">
                <p className="text-sm text-ink leading-none">{user.name}</p>
                <p className="text-xs text-inksoft">{user.email}</p>
              </div>
            </div>
          )}
          <button onClick={doLogout} className="btn-ghost text-sm" data-testid="dashboard-logout">
            <LogOut size={14} /> Salir
          </button>
          {user?.is_admin && (
            <Link to="/admin" className="btn-ghost text-sm border border-warmgold/50" data-testid="dashboard-admin-btn" title="Panel de administrador">
              <Shield size={14} /> Admin
            </Link>
          )}
          <Link to="/crear" className="btn-primary" data-testid="dashboard-crear-btn">
            <Plus size={16} /> Nueva
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-6 pb-24">
        <p className="overline">Tu panel privado</p>
        <h1 className="headline text-5xl md:text-6xl text-ink leading-tight mt-3">
          Todo tu magic
          <span className="script text-warmgold text-6xl md:text-7xl ml-2">book</span>
        </h1>

        <div className="mt-10 grid grid-cols-3 gap-6 md:gap-10 max-w-2xl">
          <Stat label="Invitaciones" value={invs.length} />
          <Stat label="Personas confirmadas" value={totalConfirmed} />
          <Stat label="Registros totales" value={totalRsvps} />
        </div>

        <div className="mt-14 space-y-4">
          {loading ? (
            <div className="grid place-items-center py-16">
              <Loader2 className="animate-spin text-warmgold" size={28} />
            </div>
          ) : invs.length === 0 ? (
            <div className="text-center py-20">
              <p className="script text-warmgold text-3xl">Aún no tienes invitaciones</p>
              <p className="text-inksoft mt-3">Crea la primera y compártela por WhatsApp.</p>
              <Link to="/crear" className="btn-primary mt-8">Crear invitación</Link>
            </div>
          ) : (
            invs.map((i) => {
              const theme = getTheme(i.theme);
              const invRsvps = rsvps[i.id] || [];
              const confirmed = invRsvps.filter((r) => r.attending).reduce((a, r) => a + (r.guests_count || 1), 0);
              const declined = invRsvps.filter((r) => !r.attending).length;
              const isExp = expanded === i.id;
              return (
                <div key={i.id} className="glass rounded-2xl p-4 md:p-5" data-testid={`inv-row-${i.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-20 md:w-20 md:h-24 rounded-xl overflow-hidden bg-sand shrink-0">
                      {(i.generated_bg_url || i.hero_image_url) && (
                        <img src={resolveImageUrl(i.generated_bg_url || i.hero_image_url)} alt={i.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="script text-warmgold text-lg leading-none">{theme?.name}</p>
                      <p className="headline text-ink text-xl md:text-2xl truncate">{i.title}</p>
                      <p className="text-inksoft text-xs mt-1">
                        {i.event_date || "sin fecha"} · {formatTime12h(i.event_time) || ""} · <span className="text-ink font-medium">{confirmed}</span> personas · {declined} no
                      </p>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-end">
                      <button onClick={() => setExpanded(isExp ? null : i.id)} className={`btn-ghost p-2 md:px-3 text-sm ${isExp ? "text-warmgold" : ""}`} data-testid={`toggle-rsvps-${i.id}`}>
                        <Users size={14} /> {invRsvps.length}
                        <ChevronDown size={14} className={`transition-transform ${isExp ? "rotate-180" : ""}`} />
                      </button>
                      <button onClick={() => copyLink(i.slug)} className="btn-ghost p-2 md:px-3" title="Copiar link" data-testid={`copy-${i.id}`}>
                        <Copy size={16} />
                      </button>
                      <Link to={`/i/${i.slug}`} className="btn-ghost p-2 md:px-3" title="Ver" data-testid={`view-${i.id}`}>
                        <Eye size={16} />
                      </Link>
                      <Link to={`/editor/${i.id}`} className="btn-ghost p-2 md:px-3" title="Editar" data-testid={`edit-${i.id}`}>
                        <Edit size={16} />
                      </Link>
                      <button onClick={() => remove(i.id)} className="btn-ghost p-2 md:px-3 text-red-500 hover:text-red-700" title="Eliminar" data-testid={`del-${i.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {isExp && (
                    <div className="mt-5 border-t border-warmgold/20 pt-4">
                      {invRsvps.length === 0 ? (
                        <p className="text-inksoft text-sm text-center py-4">Aún no hay confirmaciones.</p>
                      ) : (
                        <div className="space-y-2">
                          {invRsvps.map((r) => (
                            <div key={r.id} className="flex items-start gap-3 bg-white/60 rounded-xl p-3">
                              <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${r.attending ? "bg-sage" : "bg-terracotta"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <p className="text-ink font-medium">{r.guest_name}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.attending ? "bg-sage/20 text-olive" : "bg-terracotta/20 text-terracotta"}`}>
                                    {r.attending ? `Sí (${r.guests_count})` : "No"}
                                  </span>
                                  {r.prediction && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-warmgold/20 text-warmgold_dark">
                                      {r.prediction === "girl" ? "💗 niña" : "💙 niño"}
                                    </span>
                                  )}
                                </div>
                                {r.message && <p className="text-inksoft text-sm mt-1 italic">&ldquo;{r.message}&rdquo;</p>}
                                <p className="text-xs text-inksoft/70 mt-1">{new Date(r.created_at).toLocaleString("es-MX")}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="headline text-4xl md:text-5xl text-ink">{value}</p>
      <p className="text-xs uppercase tracking-widest text-inksoft mt-1">{label}</p>
    </div>
  );
}
